<!-- 
Dialog for setting payment status for a levy collection provider.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog sm">
    <v-form ref="form">
      <v-card>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>Set payment status</v-toolbar-title>
        </v-toolbar>
        <our-busy-indicator :show="state.saving"/>
        <v-card-text>
          <our-loading-indicator v-if="loading" />
          <v-container v-else grid-list-md pa-0>
            <v-layout wrap>
              <v-flex xs12>
                <v-select 
                  v-if="show"
                  :items="paymentStatuses" :value="details.assignedPaymentStatus"
                  autofocus
                  label="Payment status*"
                  @input="update('assignedPaymentStatus', $event)"
                  @keydown.enter.native.prevent
                />
              </v-flex>
              <v-flex v-if="isDeemedLate" xs12>
                <v-text-field 
                  :value="details.latePaymentDays" 
                  :rules="[rules.required,rules.integer,rules.greaterThanZero,rules.max1000]" 
                  label="Days late*" 
                  hint="This value will become the provider's late payment measure for the levy collection next year"
                  persistent-hint
                  @input="update('latePaymentDays', $event)"
                />
              </v-flex>
            </v-layout>
          </v-container>
          <our-required-field-indicator v-if="!loading"/>
          <v-alert :value="isDeemedLate" class="mt-3" type="warning">
            Deeming a payment as late will automatically send a notice to the provider. 
            Make sure you are happy with the <i>late payment notice</i> template before you save.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn :disabled="state.saving" flat @click="hide">Cancel</v-btn>
          <v-btn 
            :disabled="state.saving" 
            flat 
            color="primary" 
            type="submit" 
            @click.prevent="save"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form" :state-error="state.error" />
  </v-dialog>
</template>

<script>
import * as action from "@/store/levy-collection-providers/action-types";
import * as mutation from "@/store/levy-collection-providers/mutation-types";
import ruleMixins from "@/mixins/rule.mixins";
import lookupMixins from "@/mixins/lookup.mixins";

export default {
  mixins: [ruleMixins, lookupMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    levyCollectionId: {
      type: Number,
      required: true
    },
    providerId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      showValidationDialog: false,
      loading: false,
      rules: {
        max1000: v => (v >= 1000 ? "Value must be less than 1000" : true)
      }
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollectionProviders.assignedPaymentStatus;
    },
    details() {
      return this.state.data;
    },
    isDeemedLate() {
      return this.details.assignedPaymentStatus == "DeemedPaidLate";
    },
    paymentStatuses() {
      return this.getLookupsWithAttribute(
        this.lookups.assignedPaymentStatuses,
        "ApplicableDuringSettingPaymentStatusTask"
      );
    }
  },
  watch: {
    async show(nowShowing) {
      if (nowShowing) {
        this.loading = true;
        await this.$store.dispatch(action.VIEW, {
          levyCollectionId: this.levyCollectionId,
          providerId: this.providerId
        });
        this.$store.commit(mutation.ASSIGNED_PAYMENT_STATUS_INITIALISE);
        this.loading = false;
      }
    }
  },
  methods: {
    async save() {
      const data = {
        levyCollectionId: this.levyCollectionId,
        providerId: this.providerId
      };
      if (this.$refs.form.validate() && (await this.$store.dispatch(action.ASSIGNED_PAYMENT_STATUS_SAVE, data))) {
        this.hide();
      } else {
        this.showValidationDialog = true;
      }
    },
    hide() {
      this.$emit("update:show", false);
    },
    update(field, value) {
      this.$store.commit(mutation.ASSIGNED_PAYMENT_STATUS_UPDATE, { [field]: value });
    }
  }
};
</script>
